<template>
  <div>
    <!-- Dialog principal de productos importados -->
    <div v-if="showJsonLoading && !processing" class="json-review-overlay">
      <v-card class="json-dialog-card">
        <v-card-title style="padding-left: 25px; padding-bottom: 25px;" class="text-h6">
          Procesando productos del proveedor {{ providerInfo?.nombreComercial }}
          <v-tooltip bottom v-if="!providerMatches && providerInfo">
            <template v-slot:activator="{ on, attrs }">
              <v-icon style="margin: 10px; font-size: 20px;" v-bind="attrs" v-on="on" color="#f29d35">
                mdi-alert-circle
              </v-icon>
            </template>
            <span style="font-size: 12px;">El proveedor seleccionado no coincide</span>
          </v-tooltip>
        </v-card-title>

        <v-card-text>
        <v-row v-if="selectedProducts.length > 0" class="mb-3">
          <v-col cols="12">
            <v-alert type="info" dense text>
              <v-row align="center">
                <v-col>
                  {{ selectedProducts.length }} producto(s) sin vincular seleccionado(s)
                </v-col>
                <v-col cols="auto">
                  <v-btn color="primary" @click="showCreateProductScreen = true">
                    <v-icon left>mdi-plus-circle</v-icon>
                    Agregar Productos
                  </v-btn>
                </v-col>
              </v-row>
            </v-alert>
          </v-col>
        </v-row>
        <v-row
          v-if="internalProductsData.some((product) => product.error.code)"
          class="mb-3"
        >
          <v-col cols="12">
            <v-alert type="info" dense outlined>
              Puede marcar las casillas de los productos sin vincular para crearlos como nuevos productos.
            </v-alert>
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="12">
            <DxDataGrid
              :data-source="internalProductsData"
              key-expr="id_grid"
              class="grid-json"
              :ref="'productGrid'"
              @selection-changed="onSelectionChanged"
              @cell-prepared="onProductGridCellPrepared"
            >
              <DxPaging :page-size="5" />
              <DxColumn data-field="code" caption="Código" width="15%" alignment="left"></DxColumn>
              <DxColumn data-field="id" caption="ID" width="10%" alignment="left"></DxColumn>
              <DxColumn data-field="description" caption="Descripción" width="40%" alignment="left"></DxColumn>
              <DxColumn data-field="quantity" caption="Cantidad" width="10%" alignment="right"></DxColumn>
              <DxColumn data-field="price" caption="Precio" width="10%" alignment="right"></DxColumn>
              <DxColumn cell-template="cell-error" data-field="error" caption="Estado" width="10%"></DxColumn>
              <DxSelection mode="multiple" :show-check-boxes-mode="'always'" :allow-select-all="false" />
              
              <template #cell-error="{ data }">
                <v-tooltip bottom>
                  <template v-slot:activator="{ on, attrs }">
                    <v-icon 
                      style="font-size: 20px;" 
                      v-bind="attrs" 
                      v-on="on" 
                      v-if="data.data.error.code" 
                      color="red darken-4" 
                      @click.prevent.stop="onClickErrorJsonData(data.data)"
                    >
                      mdi-alert-circle
                    </v-icon>
                    <v-icon style="font-size: 20px;" v-if="!data.data.error.code">mdi-check-circle</v-icon>
                  </template>
                  <span style="font-size: 12px;">{{ data.data.error.message }}</span>
                </v-tooltip>
              </template>

            </DxDataGrid>
          </v-col>
        </v-row>
      </v-card-text>
      
      <v-card-actions>
        <v-col class="text-right">
          <v-btn @click="handleJsonDataLoad" class="btndisable">
            Continuar
          </v-btn>
        </v-col>
      </v-card-actions>
      </v-card>
    </div>

    <div v-else-if="showJsonLoading && processing" class="json-processing-overlay">
      <v-card class="json-dialog-card json-processing-card">
        <v-card-title style="padding-left: 25px; padding-bottom: 25px;" class="text-h6">
          Procesando productos del proveedor {{ providerInfo?.nombreComercial }}
        </v-card-title>
        <v-card-text class="py-12">
          <div class="d-flex flex-column align-center justify-center">
            <v-progress-circular
              indeterminate
              size="70"
              width="6"
              color="primary"
            />
            <div class="mt-5 text-center">
              Buscando coincidencias en la API. Este proceso puede tardar algunos segundos.
            </div>
          </div>
        </v-card-text>
      </v-card>
    </div>

    <!-- Dialog de vinculación de producto -->
    <v-dialog v-model="showProductLinking" max-width="85%">
      <v-card>
        <v-card-title class="text-h6">
          Detalle de errores en el producto {{ selectedProduct?.code }}:
        </v-card-title>
        
        <v-card-text>
          <v-row>
            <v-col cols="12">
              <div>
                <p style="margin: 0; padding-top: 25px; padding-bottom: 25px; font-size: 16px;">
                  {{ selectedProduct?.error.message }}, debe seleccionar uno para continuar.
                </p>
                <v-row class="mb-4">
                  <v-col cols="10" class="d-flex align-center">
                    <v-text-field
                      v-model="searchQueryBarCodes"
                      label="Escriba aqui y presione enter para buscar"
                      @keyup.enter="applyGeneralFilterBarCodes"
                      outlined
                      dense
                      :disabled="loadingRemoteSearch"
                    ></v-text-field>
                  </v-col>
                  <v-col cols="2" class="d-flex">
                    <v-btn color="primary" @click="applyGeneralFilterBarCodes" :loading="loadingRemoteSearch" :disabled="loadingRemoteSearch">
                      Buscar
                    </v-btn>
                  </v-col>
                </v-row>
                <v-progress-linear
                  v-if="loadingRemoteSearch"
                  indeterminate
                  color="primary"
                  class="mb-4"
                />
                <div v-if="loadingRemoteSearch" class="d-flex flex-column align-center justify-center py-10">
                  <v-progress-circular
                    indeterminate
                    size="60"
                    width="5"
                    color="primary"
                  />
                  <div class="mt-4 text-center">
                    Buscando productos en el backend.
                  </div>
                </div>
                <div v-else style="overflow-x: auto;">
                  <DxDataGrid
                    :data-source="filteredBarCodes"
                    key-expr="ID_Item"
                    class="grid-update"
                    column-auto-width
                    allow-column-resizing
                    column-resizing-mode="widget"
                  >
                    <DxPaging :page-size="5" />
                    <DxColumn cell-template="cell-select" caption="" width="80px"></DxColumn>
                    <DxColumn data-field="ID_Item" caption="ID" width="130px"></DxColumn>
                    <DxColumn data-field="BarCode" caption="Codigo alterno" width="130px"></DxColumn>
                    <DxColumn data-field="ItemName" caption="Nombre de Producto" width="250px" cell-template="cell-template"></DxColumn>
                    <DxColumn data-field="ItemComercialName" caption="Nombre Comercial" width="250px" cell-template="cell-template"></DxColumn>
                    <DxColumn data-field="inventory_warehouse.Warehouse" caption="Almacén" width="200px"></DxColumn>
                    <DxColumn data-field="Lote" caption="Lote" width="100px"></DxColumn>
                    <DxColumn data-field="ItemCurrentQuantity" caption="Existencias" width="100px"></DxColumn>
                    <DxColumn data-field="AverageCost" caption="Costo Promedio" width="150px"></DxColumn>
                    
                    <template #cell-template="{ data }">
                      <div class="cell-centered">
                        {{ data.value }}
                      </div>
                    </template>

                    <template #cell-select="{ data }">
                      <v-btn color="blue darken-1" text @click="handleJsonDataFixes(data.data)">
                        <v-icon style="font-size: 20px;">mdi-plus</v-icon>
                      </v-btn>
                    </template>                 
                  </DxDataGrid>
                </div>
              </div>
            </v-col>
          </v-row>
          <p style="margin: 0; padding-top: 10px; padding-bottom: 10px; font-size: 16px;">
            Si no encuentra el producto, deberá agregarlo manualmente a este proveedor desde la pestaña de productos y luego volver a intentar.
          </p>
        </v-card-text>
        
        <v-card-actions style="padding: 16px;">
          <v-spacer></v-spacer>
          <v-btn class="btndisable" @click="closeLinkingDialog">Cerrar</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Nuevo Dialog para crear productos -->
    <v-dialog v-model="showCreateProductScreen" max-width="1400px" persistent>
      <v-card>
        <v-card-title class="primary white--text">
          <v-icon color="white" class="mr-2">mdi-package-variant-plus</v-icon>
          Crear Productos ({{ selectedProducts.length }})
          <v-spacer></v-spacer>
          <v-btn icon color="white" @click="closeCreateProductScreen">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text class="pt-4">
          <v-row>
            <v-col cols="12" md="6">
              <v-checkbox
                v-model="showAdvancedFields"
                label="Mostrar campos de categorización (Familia, Categoría, Subcategoría, Marca)"
                dense
                hide-details
              ></v-checkbox>
            </v-col>
            <v-col cols="12" md="6" v-if="showAdvancedFields">
              <v-checkbox
                v-model="applyCategorizationToAll"
                label="Aplicar misma categorización a todos los productos"
                dense
                hide-details
                color="primary"
              ></v-checkbox>
            </v-col>
          </v-row>
          
          <v-row>
            <v-col cols="12">
              <DxDataGrid
                :data-source="selectedProducts"
                key-expr="id_grid"
                class="grid-json"
                :ref="'createProductGrid'"
                @row-updating="onProductRowUpdating"
                @editor-preparing="onEditorPreparing"
                :column-auto-width="true"
              >
                <DxEditing
                  :allow-updating="true"
                  mode="cell"
                />
                <DxPaging :page-size="10" />
                <DxColumn data-field="code" caption="Código" width="100" :allow-editing="false"></DxColumn>
                <DxColumn data-field="description" caption="Nombre del Artículo" width="400" :allow-editing="true">
                  <DxRequiredRule message="El nombre del artículo es requerido" />
                  <DxStringLengthRule :max="250" message="El nombre del artículo no puede exceder 250 caracteres" />
                </DxColumn>
                <DxColumn 
                  data-field="editablePrice" 
                  caption="Precio de venta" 
                  width="110" 
                  alignment="right"
                  data-type="number"
                  :allow-editing="true"
                >
                  <DxFormat type="currency" :precision="2" />
                </DxColumn>
                <DxColumn 
                  data-field="editableSuggestedPrice" 
                  caption="Precio Sugerido" 
                  width="120" 
                  alignment="right"
                  data-type="number"
                  :allow-editing="true"
                >
                  <DxFormat type="currency" :precision="2" />
                </DxColumn>
                <DxColumn 
                  data-field="editableItemType" 
                  caption="Tipo" 
                  width="130"
                  :allow-editing="true"
                >
                  <DxLookup
                    :data-source="declarableOptions"
                    display-expr="text"
                    value-expr="value"
                  />
                </DxColumn>
                <!--DxColumn data-field="quantity" caption="Cantidad (JSON)" width="110" alignment="right" :allow-editing="false"></DxColumn-->
                <DxColumn data-field="price" caption="Costo (JSON)" width="110" alignment="right" :allow-editing="false">
                  <DxFormat type="currency" :precision="2" />
                </DxColumn>
                
                <!-- Columnas adicionales de categorización -->
                <DxColumn 
                  v-if="showAdvancedFields"
                  data-field="editableFamily" 
                  caption="Familia" 
                  width="150"
                  :allow-editing="true"
                >
                  <DxLookup
                    :data-source="families"
                    display-expr="text"
                    value-expr="value"
                  />
                </DxColumn>
                <DxColumn 
                  v-if="showAdvancedFields"
                  data-field="editableCategory" 
                  caption="Categoría" 
                  width="150"
                  :allow-editing="true"
                >
                  <DxLookup
                    :data-source="categories"
                    display-expr="text"
                    value-expr="value"
                  />
                </DxColumn>
                <DxColumn 
                  v-if="showAdvancedFields"
                  data-field="editableSubcategory" 
                  caption="Subcategoría" 
                  width="150"
                  :allow-editing="true"
                >
                  <DxLookup
                    :data-source="subcategories"
                    display-expr="text"
                    value-expr="value"
                  />
                </DxColumn>
                <DxColumn 
                  v-if="showAdvancedFields"
                  data-field="editableBrand" 
                  caption="Marca" 
                  width="150"
                  :allow-editing="true"
                >
                  <DxLookup
                    :data-source="brands"
                    display-expr="text"
                    value-expr="value"
                  />
                </DxColumn>
              </DxDataGrid>
            </v-col>
          </v-row>
        </v-card-text>

        <v-card-actions class="px-6 pb-4">
          <v-spacer></v-spacer>
          <v-btn @click="closeCreateProductScreen" outlined>
            <v-icon left>mdi-close</v-icon>
            Cancelar
          </v-btn>
          <v-btn 
            color="primary" 
            @click="createProducts"
            :disabled="!canCreateProducts"
          >
            <v-icon left>mdi-content-save</v-icon>
            Crear {{ selectedProducts.length }} Producto(s)
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>


  </div>
</template>

<script>
import { 
  DxDataGrid, 
  DxColumn, 
  DxPaging, 
  DxSelection,
  DxEditing,
  DxLookup,
  DxFormat
} from "devextreme-vue/data-grid";
import { DxStringLengthRule, DxRequiredRule } from "devextreme-vue/validator";
import { userData } from "@/api/Currentuserdata";

export default {
  name: "JsonProductManager",
  components: {
    DxDataGrid,
    DxColumn,
    DxPaging,
    DxSelection,
    DxEditing,
    DxLookup,
    DxFormat,
    DxStringLengthRule,
    DxRequiredRule,
  },
  props: {
    value: {
      type: Boolean,
      default: false
    },
    productsData: {
      type: Array,
      default: () => []
    },
    providerInfo: {
      type: Object,
      default: null
    },
    providerNit: {
      type: String,
      default: ''
    },
    providerId: {
      type: [String, Number],
      default: null
    },
    noProvider: {
      type: Boolean,
      default: false
    },
    allProvidersInventoryItems: {
      type: Array,
      default: () => []
    },
    processing: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      showJsonLoading: false,
      showProductLinking: false,
      showCreateProductScreen: false,
      selectedProduct: null,
      selectedProducts: [],
      searchQueryBarCodes: '',
      loadingRemoteSearch: false,
      internalProductsData: [],
      filteredBarCodes: [],
      remoteSearchCache: {},
      showAdvancedFields: false,
      applyCategorizationToAll: false,
      declarableOptions: [
        { text: 'No Consumible', value: 'No Consumible' },
        { text: 'Consumible', value: 'Consumible' }
      ],
      families: [],
      categories: [],
      subcategories: [],
      brands: []
    };
  },
  computed: {
    providerMatches() {
      if (!this.providerInfo || !this.providerNit) return true;
      return this.providerInfo.nit === this.providerNit;
    },
    canCreateProducts() {
      // Verificar que todos los productos tengan precios mayores a 0
      return this.selectedProducts.every(product => {
        const price = parseFloat(product.editablePrice) || 0;
        const suggestedPrice = parseFloat(product.editableSuggestedPrice) || 0;
        return price > 0 && suggestedPrice > 0;
      });
    }
  },
  watch: {
    value(newVal) {
      this.showJsonLoading = newVal;
    },
    showJsonLoading(newVal) {
      this.$emit('input', newVal);
    },
    productsData: {
      immediate: true,
      deep: true,
      handler(newVal) {
        if (newVal && newVal.length > 0) {
          this.internalProductsData = newVal.map((item, index) => ({
            ...item,
            id_grid: item.code + (index)
          }));
          //this.internalProductsData = [...newVal];
        } else {
          this.internalProductsData = [];
        }
      }
    },
    allProvidersInventoryItems: {
      immediate: true,
      handler(newVal) {
        this.filteredBarCodes = [...newVal];
      }
    }
  },
  mounted() {
    this.loadCatalogs();
  },
  methods: {
    async loadCatalogs() {
      try {
        console.log('Cargando catálogos...');
        
        // Cargar familias
        const familiesRes = await this.$API.itemtypes.getFamiliesHistory();
        this.families = familiesRes.map(f => ({
          text: f.name,
          value: f.id
        }));
        console.log('Familias cargadas:', this.families);

        // Cargar categorías completas (se filtrarán por familia)
        const categoriesRes = await this.$API.itemtypes.getCategoriesHistory();
        this.categories = categoriesRes.map(c => ({
          text: c.name,
          value: c.id,
          familyId: c.id_family // Guardar relación con familia
        }));
        console.log('Categorías cargadas:', this.categories);

        // Cargar subcategorías completas (se filtrarán por categoría)
        const subcategoriesRes = await this.$API.itemtypes.getSubsHistory();
        this.subcategories = subcategoriesRes.map(s => ({
          text: s.name,
          value: s.id,
          categoryId: s.id_cat // Guardar relación con categoría
        }));
        console.log('Subcategorías cargadas:', this.subcategories);

        // Cargar marcas
        const brandsRes = await this.$API.itemtypes.getBrands();
        this.brands = brandsRes.map(b => ({
          text: b.brand,
          value: b.id
        }));
        console.log('Marcas cargadas:', this.brands);
      } catch (error) {
        console.error('Error cargando catálogos:', error);
        this.$emit('show-alert', {
          type: 'danger',
          title: 'Error',
          message: 'No se pudieron cargar los catálogos de categorización: ' + error.message
        });
      }
    },

    getCategoriesByFamily(familyId) {
      if (!familyId) return [];
      return this.categories.filter(c => c.familyId === familyId);
    },

    getSubcategoriesByCategory(categoryId) {
      if (!categoryId) return [];
      return this.subcategories.filter(s => s.categoryId === categoryId);
    },

    getCategoryDataSource(options) {
      // options.data contiene la fila actual
      if (options && options.data && options.data.editableFamily) {
        return this.getCategoriesByFamily(options.data.editableFamily);
      }
      return [];
    },

    getSubcategoryDataSource(options) {
      // options.data contiene la fila actual
      if (options && options.data && options.data.editableCategory) {
        return this.getSubcategoriesByCategory(options.data.editableCategory);
      }
      return [];
    },

    calculateCategoryCell(rowData) {
      return rowData.editableCategory;
    },

    calculateSubcategoryCell(rowData) {
      return rowData.editableSubcategory;
    },

    onEditorPreparing(e) {
      // Controlar edición cuando applyCategorizationToAll está activo
      const categorizationFields = ['editableFamily', 'editableCategory', 'editableSubcategory', 'editableBrand'];
      if (this.applyCategorizationToAll && categorizationFields.includes(e.dataField)) {
        // Solo permitir edición en la primera fila (rowIndex === 0)
        if (e.row && e.row.rowIndex !== 0) {
          e.editorOptions.disabled = true;
          return;
        }
      }

      // Filtrar categorías según la familia seleccionada
      if (e.dataField === 'editableCategory' && e.row && e.row.data) {
        const familyId = e.row.data.editableFamily;
        if (familyId) {
          const filteredCategories = this.getCategoriesByFamily(familyId);
          e.editorOptions.dataSource = filteredCategories;
        }
        // No establecer array vacío - dejar las categorías completas como fallback
      }

      // Filtrar subcategorías según la categoría seleccionada
      if (e.dataField === 'editableSubcategory' && e.row && e.row.data) {
        const categoryId = e.row.data.editableCategory;
        if (categoryId) {
          const filteredSubcategories = this.getSubcategoriesByCategory(categoryId);
          e.editorOptions.dataSource = filteredSubcategories;
        }
        // No establecer array vacío - dejar las subcategorías completas como fallback
      }
    },

    onClickErrorJsonData(product) {
      this.selectedProduct = product;
      this.showProductLinking = true;
    },

    getCurrentBusinessId() {
      return userData()?.businessid || "";
    },

    getTrimmedValue(value) {
      return String(value ?? "").trim();
    },

    hasLocalInventoryData() {
      return Array.isArray(this.allProvidersInventoryItems) && this.allProvidersInventoryItems.length > 0;
    },

    async searchProductsByCode(code) {
      const normalizedCode = this.getTrimmedValue(code);

      if (!normalizedCode) {
        return null;
      }

      if (Object.prototype.hasOwnProperty.call(this.remoteSearchCache, normalizedCode)) {
        return this.remoteSearchCache[normalizedCode];
      }

      try {
        const response = await this.$API.products.getProductsToBillForPurchaseOrdersFiltered({
          business: this.getCurrentBusinessId(),
          provider: this.providerId,
          noProvider: this.noProvider,
          filter: normalizedCode,
          showAllRecords: false,
        });

        const products = Array.isArray(response) ? response : [];
        this.remoteSearchCache[normalizedCode] = products;
        return this.remoteSearchCache[normalizedCode];
      } catch (error) {
        this.remoteSearchCache[normalizedCode] = null;
        return null;
      }
    },

    handleJsonDataFixes(inventoryItem) {
      const productCode = this.selectedProduct.code;
      
      const productIndex = this.internalProductsData.findIndex(p => p.code === productCode);
      if (productIndex !== -1) {
        this.internalProductsData[productIndex] = {
          ...this.internalProductsData[productIndex],
          id: inventoryItem.ID_Item,
          warehouse: inventoryItem.inventory_warehouse?.Warehouse || 'Sin almacén',
          ID_Warehouse: inventoryItem.inventory_warehouse?.ID_Warehouse,
          error: {
            code: null,
            message: ''
          },
          newBarcode: true
        };

        this.$emit('product-linked', {
          product: this.internalProductsData[productIndex],
        });
      }

      this.closeLinkingDialog();
    },

    async applyGeneralFilterBarCodes() {
      const query = this.getTrimmedValue(this.searchQueryBarCodes).toLowerCase();

      if (!query) {
        this.filteredBarCodes = [...this.allProvidersInventoryItems];
        return;
      }

      this.loadingRemoteSearch = true;

      try {
        const localMatches = this.allProvidersInventoryItems.filter((item) => {
          return (
            this.getTrimmedValue(item.BarCode).toLowerCase().includes(query) ||
            this.getTrimmedValue(item.ID_Item).toLowerCase().includes(query) ||
            this.getTrimmedValue(item.ItemName).toLowerCase().includes(query) ||
            this.getTrimmedValue(item.ItemComercialName).toLowerCase().includes(query)
          );
        });

        if (localMatches.length || this.hasLocalInventoryData()) {
          this.filteredBarCodes = localMatches;
          return;
        }

        const remoteMatches = await this.searchProductsByCode(query);
        this.filteredBarCodes = Array.isArray(remoteMatches) ? remoteMatches : [];
      } finally {
        this.loadingRemoteSearch = false;
      }
    },

    handleJsonDataLoad() {
      const validProducts = this.internalProductsData.filter(p => !p.error.code);
      const invalidProducts = this.internalProductsData.filter(p => p.error.code);

      if (validProducts.length === 0) {
        this.$emit('show-alert', {
          type: 'warning',
          title: 'Sin productos válidos',
          message: 'No hay productos válidos para cargar. Por favor, vincule al menos un producto.'
        });
        return;
      }

      this.$emit('load-products', {
        validProducts,
        invalidProducts
      });

      this.showJsonLoading = false;
    },

    closeLinkingDialog() {
      this.showProductLinking = false;
      this.selectedProduct = null;
      this.searchQueryBarCodes = '';
      this.filteredBarCodes = [...this.allProvidersInventoryItems];
    },

    onProductGridCellPrepared(e) {
      if (e.rowType !== 'data' || e.column?.command !== 'select') {
        return;
      }

      if (!e.data?.error?.code) {
        e.cellElement.innerHTML = '';
        e.cellElement.style.pointerEvents = 'none';
      }
    },

    onSelectionChanged(e) {
      // Solo permitir selección de productos con error
      const selectedRows = e.selectedRowsData.filter(row => row.error.code);
      
      // Inicializar campos editables para productos seleccionados con precios en 0
      this.selectedProducts = selectedRows.map((product, index) => ({
        ...product,
        editablePrice: 0,
        editableSuggestedPrice: 0,
        editableItemType: 'No Consumible',
        editableFamily: null,
        editableCategory: null,
        editableSubcategory: null,
        editableBrand: null,
        id_grid: product.code + index
      }));
      
      // Si hay productos válidos seleccionados, deseleccionarlos
      if (e.selectedRowsData.length !== selectedRows.length) {
        this.$nextTick(() => {
          const gridInstance = this.$refs.productGrid?.instance;
          if (gridInstance) {
            gridInstance.clearSelection();
            selectedRows.forEach(row => {
              gridInstance.selectRows([row.code], true);
            });
          }
        });
      }
    },

    closeCreateProductScreen() {
      this.showCreateProductScreen = false;
      this.showAdvancedFields = false;
    },

    onProductRowUpdating(e) {
      // Cuando se actualiza el precio con IVA, actualizar el precio sugerido automáticamente
      if (e.newData.editablePrice !== undefined) {
        const newPrice = parseFloat(e.newData.editablePrice) || 0;
        const currentSuggestedPrice = e.oldData.editableSuggestedPrice || 0;
        // Solo actualizar el precio sugerido si aún no ha sido modificado manualmente
        // o si el precio sugerido es 0
        if (currentSuggestedPrice === 0 || e.newData.editableSuggestedPrice === undefined) {
          e.newData.editableSuggestedPrice = newPrice;
        }
      }

      // Si applyCategorizationToAll está activo y se está editando la primera fila
      const isFirstRow = this.selectedProducts.findIndex(p => p.code === e.key) === 0;
      if (this.applyCategorizationToAll && isFirstRow) {
        // Aplicar cambios de familia, categoría, subcategoría o marca a todos los productos
        if (e.newData.editableFamily !== undefined || 
            e.newData.editableCategory !== undefined || 
            e.newData.editableSubcategory !== undefined || 
            e.newData.editableBrand !== undefined) {
          
          this.$nextTick(() => {
            this.selectedProducts.forEach((product, index) => {
              if (index > 0) { // Saltar la primera fila
                if (e.newData.editableFamily !== undefined) {
                  product.editableFamily = e.newData.editableFamily;
                  product.editableCategory = null;
                  product.editableSubcategory = null;
                }
                if (e.newData.editableCategory !== undefined) {
                  product.editableCategory = e.newData.editableCategory;
                  product.editableSubcategory = null;
                }
                if (e.newData.editableSubcategory !== undefined) {
                  product.editableSubcategory = e.newData.editableSubcategory;
                }
                if (e.newData.editableBrand !== undefined) {
                  product.editableBrand = e.newData.editableBrand;
                }
              }
            });
          });
        }
      }

      // Cuando se cambia la familia, limpiar categoría y subcategoría
      if (e.newData.editableFamily !== undefined && e.oldData.editableFamily !== e.newData.editableFamily) {
        e.newData.editableCategory = null;
        e.newData.editableSubcategory = null;
      }

      // Cuando se cambia la categoría, limpiar subcategoría
      if (e.newData.editableCategory !== undefined && e.oldData.editableCategory !== e.newData.editableCategory) {
        e.newData.editableSubcategory = null;
      }
    },

    createProducts() {
      if (this.selectedProducts.length === 0) return;
      
      // Validar que todos los precios sean mayores a 0
      const hasInvalidPrices = this.selectedProducts.some(product => {
        const price = parseFloat(product.editablePrice) || 0;
        const suggestedPrice = parseFloat(product.editableSuggestedPrice) || 0;
        return price <= 0 || suggestedPrice <= 0;
      });

      if (hasInvalidPrices) {
        this.$emit('show-alert', {
          type: 'warning',
          title: 'Precios inválidos',
          message: 'Todos los productos deben tener Precio con IVA y Precio Sugerido mayores a 0.'
        });
        return;
      }
      
      // Procesar directamente con los datos editados en la tabla
      const productsToCreate = this.selectedProducts.map(product => {
        // Asegurar que los precios sean números válidos
        const basePrice = parseFloat(product.editablePrice || product.price || 0);
        const suggestedPrice = parseFloat(product.editableSuggestedPrice || (basePrice * 1.3) || 0);
        
        return {
          code: product.code,
          description: product.description || '',
          price: Number(basePrice.toFixed(2)),
          suggestedPrice: Number(suggestedPrice.toFixed(2)),
          itemType: product.editableItemType || 'No Consumible',
          quantity: parseInt(product.quantity) || 0,
          family: product.editableFamily || null,
          category: product.editableCategory || null,
          subcategory: product.editableSubcategory || null,
          brand: product.editableBrand || null
        };
      });
      
      console.log('Productos a crear:', productsToCreate);
      
      // Emitir evento para crear productos
      this.$emit('create-products', {
        products: productsToCreate
      });
      
      // Cerrar dialog
      this.closeCreateProductScreen();
      
      // Limpiar selección
      this.selectedProducts = [];
      const gridInstance = this.$refs.productGrid?.instance;
      if (gridInstance) {
        gridInstance.clearSelection();
      }
    }
  }
};
</script>

<style>
.purchase-info-dialog {
  z-index: 1200 !important;
}

.json-review-overlay {
  position: fixed;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  z-index: 1000 !important;
  pointer-events: none;
  background: rgba(0, 0, 0, 0.18);
}

.json-processing-overlay {
  position: fixed;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  z-index: 1050;
  pointer-events: none;
  background: rgba(0, 0, 0, 0.28);
}

.json-dialog-card {
  background-color: #ffffff !important;
  opacity: 1 !important;
}

.json-processing-card {
  width: 100%;
  max-width: 1000px;
  pointer-events: auto;
}

.json-review-overlay > .v-card,
.json-processing-overlay > .v-card {
  width: 100%;
  max-width: 1000px;
  pointer-events: auto;
}
</style>

<style scoped>
.cell-centered {
  display: flex;
  align-items: start;
}

.btndisable {
  background-color: #41464c !important;
  color: white;
}
</style>

<style>
.grid-json .dx-datagrid-content .dx-datagrid-table .dx-row > td,
.grid-update .dx-datagrid-content .dx-datagrid-table .dx-row > td {
  vertical-align: middle !important;
  height: 50px;
}
</style>
